// Personalizer-settings runtime — shared between the storefront embed
// and the admin's preview iframe.
//
// Two responsibilities:
//   1. **Fetch** the merchant's saved settings from the public
//      `/storefront/settings/personalizer?shop=…` endpoint (storefront)
//      OR consume them from a `postMessage`/`localStorage` source
//      (admin preview iframe — see `apps/admin/preview.html`).
//   2. **Apply** them to the DOM: brand colours, fonts, CTA labels,
//      step labels, tips/save-design copy and trigger bullets. Applied
//      via a CSS-vars override block + a `[data-prs-target]`-driven
//      text swapper.
//
// This module is the source of truth for the bridge. The admin preview
// HTML pulls it in too (no more 100-line inline `<script>`). Anything
// the merchant edits in /admin/personalizer flows here.

// Subpath deliberado: `../i18n.js` trae los dos diccionarios (~21 KB)
// y este módulo viaja en el entry del storefront. Ver i18n-overrides.ts.
import { setTextOverrides, localeDelCliente } from '../i18n-overrides.js';
import { etiquetaResume } from '../trigger-labels.js';
import { fetchWithTimeout } from '../fetch-timeout.js';

export type PersonalizerSettingsLike = {
  // Brand
  accentColor?: string;
  accentColorDark?: string;
  fontFamily?: string;
  buttonRadius?: number;
  overlayBg?: string;
  /** Overlay surface palette — recolours the customizer chrome so the
   *  merchant can match their store's look instead of the stone-neutral
   *  default. Hex 6/8-digit; invalid values are ignored. Scoped to the
   *  overlay (`.bd-overlay`/`.bd-shell`) — the trigger CTA keeps its own
   *  neutrals, and the product-preview canvas stays neutral on purpose.
   *    surfaceColor → --dfp-surface  (cards / panels)
   *    panelBg      → --dfp-bg       (area behind the cards)
   *    textColor    → --dfp-text     (primary text + strong borders)
   *    borderColor  → --dfp-border   (hairline dividers) */
  surfaceColor?: string;
  panelBg?: string;
  textColor?: string;
  borderColor?: string;
  /** Merchant's own brand swatches. Surfaced at the TOP of every
   *  customer-facing colour picker (closed-palette + free-pick mode),
   *  in addition to the curated 40 cremas. Max 8 hex values; invalid
   *  entries are silently dropped at apply time. */
  brandSwatches?: string[];
  /** Background fill for empty upload-photo slots on the preview canvas
   *  (the "+ / Tu foto" placeholder). Unlike the overlay-surface palette
   *  above, this paints INSIDE the product preview. Threaded into the
   *  renderer via rctx.emptySlotColor; when unset the placeholder uses its
   *  built-in neutral grey. Hex 6/8-digit; invalid values are ignored. */
  emptySlotColor?: string;

  // Copy
  ctaLabel?: string;
  /** Texto del botón de la ficha cuando el cliente dejó un diseño a
   *  medias en este dispositivo. Ver draft-flag.ts. */
  ctaResumeLabel?: string;
  bullets?: string[];
  bottomCtaLabels?: { step1?: string; step2?: string; step3?: string };
  stepLabels?: { step1?: string; step2?: string; step3?: string };
  tipsLabel?: string;
  saveDesignLabel?: string;
  consejoText?: string;
  savedBadgeText?: string;
  brandName?: string;
  /** URL del logo de la tienda — sustituye la "K" del cubo superior
   *  izquierdo del header.  Cuando está vacío, se mantiene la inicial
   *  por defecto. La imagen se renderiza con object-fit: contain
   *  dentro del cubo (32×32 desktop, 22×22 móvil). */
  brandLogoUrl?: string;
  closeLabel?: string;
  errorGenericText?: string;
  /** Eyebrow above the product name in the overlay header
   *  ("Personalizando" by default). Lets the merchant call it
   *  "Customising" / "Personnalisation" without rebuilding. */
  eyebrowText?: string;
  /** "Back" button label in the bottom bar. */
  backLabel?: string;
  /** Canvas-tools row buttons ("Consejos" / "Compartir"). */
  tipsButtonLabel?: string;
  shareButtonLabel?: string;
  /** Loading copy inside the sidebar panes while options/formats
   *  hydrate. Two slots because the order of mount differs. */
  loadingOptionsText?: string;
  loadingFormatsText?: string;
  /** Brief flash shown between "added to cart succeeded" and the
   *  /cart redirect (~700 ms). Title is the big line, subtext the
   *  smaller "Te llevamos a la cesta…" reassurance. Empty title
   *  skips the flash entirely (instant redirect, prior behaviour). */
  cartSuccessTitle?: string;
  cartSuccessSubtext?: string;
  /** Toast shown when the customer copies the "compartir" link.
   *  Defaults to "🔗 Enlace copiado al portapapeles." */
  linkCopiedText?: string;
  /** Fallback product name shown in the overlay header when the
   *  template carries no productName.  Defaults to "Tu diseño". */
  productMetaDefaultName?: string;
  /** Hero of the legacy 3-tab fallback (when no `customizationSteps`
   *  are configured on the template).  Default "Vamos a darle forma a tu pieza"
   *  / "Elige la forma, el color y los detalles visuales." */
  legacyHeroTitle?: string;
  legacyHeroSubtext?: string;
  /** "Consejos" popover content above the canvas.  Empty array
   *  keeps the existing curated 5 bullets; non-empty replaces them
   *  wholesale.  Heading defaults to "💡 Consejos rápidos". */
  tipsHeading?: string;
  tipsBullets?: string[];
  /** Dismiss button copy inside the tips popover. */
  tipsDismissLabel?: string;

  // Feature toggles
  enableSaveDesign?: boolean;
  /** La tarjeta de dedicatoria como añadido en el paso «Formato».
   *  Apagada de fábrica — ver el comentario del mismo ajuste en
   *  `apps/api/src/routes/settings.ts`. */
  enableDedicationCard?: boolean;
  /** Handle del producto de la tarjeta. Vacío = `tarjeta-dedicatoria`. */
  dedicationCardHandle?: string;
  /** La taza a juego que se ofrece tras añadir el cuadro al carrito.
   *  Apagada de fábrica — ver el mismo ajuste en
   *  `apps/api/src/routes/settings.ts`. */
  enableMugUpsell?: boolean;
  /** «Enviar desde el móvil» — el QR bajo el hueco de la foto.
   *  ENCENDIDO de fábrica: `undefined` vale como sí, y sólo un `false`
   *  explícito lo retira. Ver `ofrecerEnvioMovil`. */
  enableEnvioMovil?: boolean;
  /** La PRENDA A JUEGO (28-sep-2026), apagada de fábrica. */
  enablePrendaUpsell?: boolean;
  prendaUpsellHandle?: string;
  prendaUpsellTitle?: string;
  /** EL LLAVERO CON NOMBRE (10-oct-2026), apagado de fábrica: hasta que
   *  el comercio publique su ficha, la fila no tendría nada que añadir.
   *  No es un añadido «a juego» — no se deriva del cuadro: el cliente lo
   *  monta en su estudio y lo que viaja es su código. */
  enableRotuloUpsell?: boolean;
  /** Handle del producto del llavero. Vacío = `llavero-con-nombre`. */
  rotuloUpsellHandle?: string;
  /** El titular de la fila. Vacío = el de fábrica (`keyring_title`). */
  rotuloUpsellTitle?: string;
  /** Test A/B: la mitad de los visitantes ve el botón de comprar SIN el
   *  precio repetido. Ver `experimentos/precio-en-el-boton.ts`. */
  /** Handle del producto de la taza. Vacío = `taza-a-juego`. */
  mugUpsellHandle?: string;
  /** Espera antes de enseñar el pop-up, en segundos. */
  mugUpsellTitle?: string;
  enableQuickSuggestions?: boolean;
  showStyleNavigator?: boolean;
  closedPaletteEnabled?: boolean;
  enableAnalytics?: boolean;
  /** Warm the customizer chunk on hover/focus/idle. Off = load on click. */
  enablePrefetch?: boolean;

  // Validation / limits
  maxPhotoMb?: number;
  /** Reject photo uploads whose shorter side is < this many pixels.
   *  0 (default) = no minimum. Print shops typically want ≥1500 px so
   *  the final PNG can be rasterised at 300 dpi. */
  minPhotoPx?: number;
  blocklist?: string[];

  // Analytics
  /** When set AND the storefront page has loaded gtag (window.gtag),
   *  the embed mirrors every internal event to GA4 with this Measurement
   *  ID. Empty string / missing = no GA mirroring (privacy-preserving
   *  default). The internal /storefront/events endpoint still receives
   *  the event unless `enableAnalytics === false`. */
  gaMeasurementId?: string;
  /** Optional prefix prepended to every gtag event name so the
   *  merchant's GA dashboard groups them ("brandeate_added_to_cart").
   *  Default empty = events fire under their raw kind name. */
  analyticsEventPrefix?: string;
  /** Outbound webhooks fanned out from the same tracker that POSTs
   *  to /storefront/events. Each enabled `{event,url}` receives a
   *  JSON body `{ shop, templateId, kind, payload, sessionId, locale,
   *  device, ts }`. Failures are swallowed (best-effort, never blocks
   *  the customer flow). Same opt-out as the internal tracker:
   *  enableAnalytics === false silences everything. */
  webhooks?: Array<{ id?: string; event?: string; url?: string; enabled?: boolean }>;
  /** ⚠️ DATO PERSONAL. Guarda el texto que escribe el cliente en cada
   *  campo, no solo el hecho de que lo editó. Es lo que permite la
   *  pestaña "Qué escriben" del panel de analítica, y es opt-in
   *  explícito: sin este flag el embed no manda el valor y el servidor
   *  lo descartaría igualmente. Los valores se purgan a los
   *  `analyticsValueRetentionDays` días (30 por defecto). Actívalo solo
   *  si tu política de privacidad lo cubre. */
  captureFieldValues?: boolean;
  /** Días que se conserva el TEXTO capturado. 0 = no guardarlo nunca
   *  (equivale a captureFieldValues off). Lo aplica el barrido del
   *  worker, no el cliente. */
  analyticsValueRetentionDays?: number;
  /** Días que se conservan las filas de analítica (el evento en sí, sin
   *  el texto). Por debajo de esto el panel deja de poder mirar atrás.
   *  180 por defecto. */
  analyticsRetentionDays?: number;

  // Trust signals (visible on the inline/legacy PDP layout). All
  // optional and individually gateable so a merchant can keep some
  // and hide the others without rewriting the whole block.
  /** Master toggle for the inline-mode trust strip + testimonials +
   *  social-proof line. Default true (preserves current behaviour). */
  enableTrustSignals?: boolean;
  /** Single chip row above the CTA. Hardcoded examples we ship by
   *  default: shipping, units sold, payment, returns. Each chip is
   *  emoji + headline + small subtext. Empty array hides the row. */
  trustChips?: Array<{ emoji: string; title: string; subtitle?: string }>;
  /** Rating row ("★ 4,9 / 5 — 1.247 opiniones verificadas"). All-or-
   *  nothing: a single string the merchant can rewrite or clear. */
  ratingText?: string;

  // Behaviour timings — exposed for kiosk / high-pressure deployments
  // that want longer celebratory moments OR snappy enterprises that
  // want everything tightened. Each value is read fresh at fire time,
  // so adjustments in /admin/personalizer take effect on the next
  // interaction without reload.
  /** Debounce window for the autosave POST. Default 600ms. */
  autosaveDebounceMs?: number;
  /** "Guardado" pulse on the header badge. Default 1400ms. */
  savedPulseMs?: number;
  /** Generic toast (link copied, etc.). Default 2400ms. */
  toastMs?: number;
  /** Cart-success flash duration before /cart redirect. Default 700ms.
   *  Range-bounded by the bridge consumer (300–3000) to avoid the
   *  merchant accidentally locking the customer in a 60s flash. */
  cartSuccessMs?: number;

  // Post-purchase resume — mirrors the EditFlowConfig type from
  // @personalizer/form-types. Folded into the personalizer-settings
  // blob (rather than its own DB column or endpoint) so a merchant
  // toggling tone of voice can do it from one screen. The embed
  // resolves merchant overrides via getCurrentSettings()?.editFlow
  // before falling back to the Shop.editFlowConfig column.
  editFlow?: {
    banner_editing_title?: string;
    banner_editing_body?:  string;
    banner_locked_title?:  string;
    banner_locked_body?:   string;
    cta_save?:             string;
    cta_saving?:           string;
    toast_saved?:          string;
    locked_mid_save?:      string;
    email_ask?:            string;
    email_cta?:            string;
    email_footnote?:       string;
  };

  // Overlay visual tuning ────────────────────────────────────────────
  /** Blur intensity (px) applied to the overlay backdrop. Default 4.
   *  0 disables the blur entirely (snappier on low-end devices). */
  overlayBlurPx?: number;
  /** "Compact / normal / rounded" corner-radius scale. Affects
   *  buttons, swatches, cards alike via CSS vars. Independent of
   *  `buttonRadius` which still sets the CTA-specific radius. */
  cornerRadiusScale?: 'compact' | 'normal' | 'rounded';
  /** "Subtle / normal / bold" drop-shadow strength. Brand-system
   *  merchants on flat designs want subtle; lifestyle merchants
   *  often want bold. */
  shadowStrength?: 'subtle' | 'normal' | 'bold';
  /** Stylesheet URL the merchant pasted in admin — typically a
   *  Google Fonts `https://fonts.googleapis.com/css2?family=…&display=swap`
   *  link, or a self-hosted `@font-face` stylesheet. We inject it as
   *  `<link rel="stylesheet">` in <head> so the font becomes available
   *  to the overlay; `fontFamily` (already exists) names the family. */
  customFontUrl?: string;

  // Device bucketing — used by the tracker to attribute events. The
  // merchant can shift the thresholds when their definition of
  // mobile/tablet diverges from the default (<600 / <1024).
  deviceBuckets?: { mobileMax?: number; tabletMax?: number };

  // Photo upload — additions to the per-layer `allowedFormats`. The
  // shop-wide list is the FLOOR (intersected with the per-layer list).
  /** Shop-wide allowlist of MIME types. Empty array = no shop-wide
   *  restriction (per-layer rules still apply). */
  allowedPhotoFormats?: string[];
  /** Target DPI used by the upload "your photo will print at X dpi"
   *  feedback. Default 300. Higher = stricter, lower = friendlier. */
  photoDpiTarget?: number;
  /** «Ampliar para que se imprima nítida (+2 €)»: cuando la foto del
   *  cliente se queda corta para el tamaño elegido, se le ofrece
   *  ampliarla con Real-ESRGAN y se cobra como una línea aparte.
   *  Apagado de fábrica. Sin `REPLICATE_API_TOKEN` en el worker el
   *  botón aparece pero el trabajo se rinde, así que hay que encender
   *  las dos cosas. */
  enableUpscale?: boolean;
  /** Handle del producto que cobra la ampliación de fotos.
   *  Vacío = `mejora-de-resolucion`. */
  upscaleHandle?: string;
  /** Handle del producto de 2 € que cobra la mejora. Vacío = el de
   *  fábrica (`mejora-de-resolucion`). Handle y no id de variante: un
   *  id se rompe en silencio el día que se repone el producto. */
  upscaleProductHandle?: string;
  /** «Un texto en la madera (+5 €)»: el rótulo que se imprime en UV
   *  sobre la franja de pino de la maqueta 3D. Se cobra con una
   *  VARIANTE del propio mapa (opción «Texto»), no con un producto
   *  aparte. Apagado de fábrica: sin esas variantes se regalaría una
   *  pasada de UV por pedido. Ver `customer-3d/texto-madera.ts`. */
  enableWoodText?: boolean;
  /** When true, the upload hint mentions that EXIF metadata
   *  (including GPS) will be stripped before upload. Default true
   *  (informational only — the server-side worker still does the
   *  actual stripping). */
  stripPhotoExif?: boolean;

  // Pricing display ──────────────────────────────────────────────────
  /** Master switch for hiding the price tag — B2B sellers commonly
   *  show "POA" (price on application) or a "Solicitar presupuesto"
   *  link in place of the price.  Modes:
   *    - 'never' (default) — always show
   *    - 'on_quote'        — show a quote CTA instead
   *    - 'always'          — hide both price + quote (display name only) */
  hidePriceMode?: 'never' | 'on_quote' | 'always';
  /** Currency symbol position: 'before' = "$10.00", 'after' = "10,00 €".
   *  Defaults to the embed's existing locale-driven behaviour. */
  currencySymbolPosition?: 'before' | 'after';

  // Loading animation ─────────────────────────────────────────────────
  /** Shop-wide loading overlay (the `.bd-loader` shown while the
   *  template fetches + first-renders). This is the BASE config; a
   *  template can override it via `doc.loadingAnimation` (only when that
   *  template opts in with `override: true`). Empty `logoUrl`/`label`
   *  inherit the merchant's brand logo (`brandLogoUrl`) + wordmark
   *  (`brandName`) so the loader is branded with zero extra setup.
   *  Read by `readShopLoading()` in brandeate/loading-overlay.ts. */
  loading?: {
    enabled?: boolean;
    /** One of the 7 styles — see LoadingStyle in brandeate/loading-overlay.ts
     *  (spinner | pulse | dots | bars | orbit | progress | glow). */
    style?: string;
    color?: string;
    background?: string;
    logoUrl?: string;
    logoLetter?: string;
    label?: string;
    hint?: string;
  };

  // Form
  extraFields?: unknown[];
  locales?: string[];
  defaultLocale?: string;

  /** Merchant copy overrides for every t()-driven string in the embed
   *  (labels, placeholders, hints, errors, section titles…). Keys are
   *  the i18n Dict keys (apps/embed/src/i18n.ts) verbatim — edited in
   *  admin → Personalizador → «Textos». Highest priority in the chain:
   *  settings.texts[key] → dict[locale][key] → ES default. Empty
   *  string = use the built-in default. */
  texts?: Record<string, string>;
  /** EN QUÉ IDIOMA ESTÁ ESCRITO el copy de arriba (dos letras: 'es',
   *  'pt'…). Con él puesto, lo que el comerciante escribió se aplica
   *  SÓLO en ese idioma y en los demás manda el diccionario.
   *
   *  Hace falta desde que la tienda tiene más de un mercado: estos
   *  campos son UN texto, no uno por idioma, así que en el escaparate
   *  portugués pisaban el diccionario y la cabecera salía en castellano
   *  («Consejos», «Guardar diseño», «Personalizando») sobre un
   *  personalizador por lo demás en portugués. Medido el 4-oct-2026 en
   *  dondefue.com: DOCE campos con texto castellano guardado.
   *
   *  Sin poner, se aplica siempre — que es lo que ha hecho siempre y lo
   *  que necesita una tienda de un solo idioma: con la compuerta
   *  encendida de fábrica, cualquier comercio perdería su copy. */
  textsLocale?: string;

  // Anything else the admin saved that we don't model here is ignored.
  [k: string]: unknown;
};

const HEX = /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const FONT_SAFE = /[^a-zA-Z0-9 _-]/g; // strip punctuation that would let CSS escape the string
const STYLE_TAG_ID = 'dfp-prs-overrides';

// ── Colour maths for accent-family derivation ──────────────────────────
// The overlay reads a whole accent FAMILY (--dfp-accent-hover / -soft /
// -ink / cta-fg), not just --dfp-accent.  Without these the family stays
// frozen on the Brandeate coral, so a merchant who picks a blue accent
// gets coral hovers + coral selected-chip backgrounds.  We derive the
// family from the single accent colour the merchant actually picks.
type Rgb = { r: number; g: number; b: number };
function hexToRgb(hex: string): Rgb | null {
  const m = hex.trim().replace(/^#/, '');
  let r: number, g: number, b: number;
  if (m.length === 3 || m.length === 4) {
    r = parseInt(m[0]! + m[0]!, 16);
    g = parseInt(m[1]! + m[1]!, 16);
    b = parseInt(m[2]! + m[2]!, 16);
  } else if (m.length === 6 || m.length === 8) {
    r = parseInt(m.slice(0, 2), 16);
    g = parseInt(m.slice(2, 4), 16);
    b = parseInt(m.slice(4, 6), 16);
  } else {
    return null;
  }
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
  return { r, g, b };
}
function toHex2(n: number): string {
  return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
}
const BLACK: Rgb = { r: 0, g: 0, b: 0 };
/** Linear mix toward `t` by `amt` (0 = unchanged, 1 = fully `t`). */
function mixRgb(c: Rgb, t: Rgb, amt: number): string {
  return `#${toHex2(c.r + (t.r - c.r) * amt)}${toHex2(c.g + (t.g - c.g) * amt)}${toHex2(c.b + (t.b - c.b) * amt)}`;
}
/** WCAG relative luminance, 0 (black) … 1 (white). */
function luminance(c: Rgb): number {
  const ch = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(c.r) + 0.7152 * ch(c.g) + 0.0722 * ch(c.b);
}

// In-memory cache so multiple consumers (trigger CTA + overlay) share
// the same fetched payload and we don't hit the network twice on the
// same page. Stays for the page's lifetime — cache-busted by reload.
const cache = new Map<string, Promise<PersonalizerSettingsLike>>();

// The most recent settings we've applied. Used by the MutationObserver
// to re-apply text overrides when the embed lazily mounts new nodes.
let lastApplied: PersonalizerSettingsLike | null = null;

/** Fetch (and cache) the merchant's settings for a given shop domain.
 *  Returns an empty object on any failure — callers must always treat
 *  every field as optional. */
export async function fetchPersonalizerSettings(
  apiBase: string,
  shop: string,
): Promise<PersonalizerSettingsLike> {
  const key = `${apiBase}|${shop}`;
  const existing = cache.get(key);
  if (existing) return existing;

  const url = `${apiBase.replace(/\/$/, '')}/storefront/settings/personalizer?shop=${encodeURIComponent(shop)}`;
  // Con timeout: el arranque del personalizador espera a esta promesa, y
  // una API que acepta la conexión y no contesta dejaba el loader girando.
  const promise = fetchWithTimeout(url, { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((json: { settings?: PersonalizerSettingsLike }) => json.settings ?? {})
    .catch((err) => {
      // Don't poison the cache on transient failures — next call retries.
      cache.delete(key);
      console.warn('[personalizer-settings] fetch failed', err);
      return {} as PersonalizerSettingsLike;
    });

  cache.set(key, promise);
  return promise;
}

/** Read the most recently applied settings. Null until first apply.
 *  Modules that gate features on a toggle (save-design button, ideas
 *  chips, photo size limit) read through this. */
export function getCurrentSettings(): PersonalizerSettingsLike | null {
  return lastApplied;
}

/** Apply CSS overrides (accent colours, font, button radius, overlay
 *  background). Safe to call repeatedly — replaces the single
 *  injected <style> tag rather than appending. */
export function applyPersonalizerCss(s: PersonalizerSettingsLike): void {
  let tag = document.getElementById(STYLE_TAG_ID);
  if (!tag) {
    tag = document.createElement('style');
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = buildPersonalizerOverrideCss(s);

  // Custom font stylesheet URL — injected as a sibling <link> in head
  // (idempotent: replaces any prior tag with the same id). The
  // family name itself still comes from `fontFamily`.
  applyCustomFontLink(s.customFontUrl);
}

/** Pure builder for the override stylesheet body — returns the CSS text
 *  that applyPersonalizerCss injects. Extracted so the var-emission
 *  contract (accent-family derivation, overlay-only surface scoping,
 *  explicit-hover-wins) is unit-testable without a DOM. */
export function buildPersonalizerOverrideCss(s: PersonalizerSettingsLike): string {
  const parts: string[] = [];

  const hasExplicitHover = !!(s.accentColorDark && HEX.test(s.accentColorDark));
  if (s.accentColor && HEX.test(s.accentColor)) {
    const vars = [
      `--dfp-accent:${s.accentColor} !important`,
      `--dfp-cta-bg:${s.accentColor} !important`,
    ];
    // Derive the rest of the accent family so hover / selected-chip /
    // deep-ink states track the merchant's colour instead of the coral
    // default. An explicit hover (accentColorDark) still wins below.
    const c = hexToRgb(s.accentColor);
    if (c) {
      vars.push(`--dfp-accent-soft:rgba(${c.r},${c.g},${c.b},0.12) !important`);
      vars.push(`--dfp-accent-tint:rgba(${c.r},${c.g},${c.b},0.20) !important`);
      vars.push(`--dfp-accent-ink:${mixRgb(c, BLACK, 0.72)} !important`);
      // White CTA text is unreadable on a light brand colour → flip to
      // dark ink once the accent is bright enough (WCAG-ish threshold).
      vars.push(`--dfp-cta-fg:${luminance(c) > 0.6 ? '#1A1916' : '#ffffff'} !important`);
      if (!hasExplicitHover) vars.push(`--dfp-accent-hover:${mixRgb(c, BLACK, 0.14)} !important`);
    }
    parts.push(`.dfp-root,.dfp-trigger-root,.bd-overlay,.bd-shell{${vars.join(';')}}`);
  }
  if (hasExplicitHover) {
    // Map to the var the overlay CTA actually reads (--dfp-accent-hover);
    // the legacy --dfp-accent-bg target had zero consumers.
    parts.push(
      '.dfp-root,.dfp-trigger-root,.bd-overlay,.bd-shell{'
      + `--dfp-accent-hover:${s.accentColorDark} !important}`,
    );
  }
  if (s.fontFamily && s.fontFamily !== 'System') {
    const safe = s.fontFamily.replace(FONT_SAFE, '');
    if (safe) {
      parts.push(
        `.dfp-root,.dfp-trigger-root,.bd-overlay,.bd-shell{font-family:"${safe}",sans-serif !important}`,
      );
    }
  }
  if (typeof s.buttonRadius === 'number' && s.buttonRadius >= 0 && s.buttonRadius <= 64) {
    const r = Math.round(s.buttonRadius);
    parts.push(
      `.dfp-trigger-cta,.bd-cta{border-radius:${r}px !important}`,
    );
  }
  if (s.overlayBg && HEX.test(s.overlayBg)) {
    parts.push(`.bd-overlay .bd-backdrop{background:${s.overlayBg} !important}`);
  }
  // Overlay surface palette — recolour cards / page bg / text / hairlines.
  // Scoped to the overlay only (the trigger CTA + deprecated inline root
  // keep their neutrals; the product-preview canvas stays neutral too).
  const surfaceVars: string[] = [];
  for (const [val, name] of [
    [s.surfaceColor, '--dfp-surface'],
    [s.panelBg, '--dfp-bg'],
    [s.textColor, '--dfp-text'],
    [s.borderColor, '--dfp-border'],
  ] as const) {
    if (typeof val === 'string' && HEX.test(val)) surfaceVars.push(`${name}:${val} !important`);
  }
  if (surfaceVars.length) parts.push(`.bd-overlay,.bd-shell{${surfaceVars.join(';')}}`);
  // Overlay backdrop blur — 0 disables, range-clamped to keep the
  // overlay legible on low-end devices.
  if (typeof s.overlayBlurPx === 'number' && Number.isFinite(s.overlayBlurPx)) {
    const blur = Math.max(0, Math.min(20, Math.round(s.overlayBlurPx)));
    parts.push(
      `.bd-overlay .bd-backdrop{backdrop-filter:blur(${blur}px) !important;-webkit-backdrop-filter:blur(${blur}px) !important}`,
    );
  }
  // Corner-radius scale — multiplies the .bd-* radii. Independent of
  // `buttonRadius` (which is CTA-specific). Compact = 0.5x, normal =
  // 1x, rounded = 1.6x.
  if (s.cornerRadiusScale === 'compact' || s.cornerRadiusScale === 'rounded') {
    const m = s.cornerRadiusScale === 'compact' ? 0.5 : 1.6;
    const r = (px: number): number => Math.round(px * m);
    parts.push(
      '.dfp-root,.dfp-trigger-root,.bd-overlay,.bd-shell{'
      + `--dfp-r-sm:${r(6)}px !important;`
      + `--dfp-r-md:${r(10)}px !important;`
      + `--dfp-r-lg:${r(14)}px !important;`
      + `--dfp-r-xl:${r(22)}px !important}`,
    );
  }
  // Shadow strength — swaps the canonical shadow scales for softer
  // or punchier variants. Brand-system merchants on flat designs
  // pick subtle; lifestyle merchants often pick bold.
  if (s.shadowStrength === 'subtle' || s.shadowStrength === 'bold') {
    const subtle = s.shadowStrength === 'subtle';
    const sm = subtle ? '0 1px 2px rgba(0,0,0,0.05)' : '0 2px 8px rgba(0,0,0,0.15)';
    const md = subtle ? '0 2px 6px rgba(0,0,0,0.06)' : '0 6px 20px rgba(0,0,0,0.18)';
    const lg = subtle ? '0 4px 12px rgba(0,0,0,0.08)' : '0 12px 36px rgba(0,0,0,0.22)';
    parts.push(
      '.dfp-root,.dfp-trigger-root,.bd-overlay,.bd-shell{'
      + `--dfp-shadow-sm:${sm} !important;`
      + `--dfp-shadow-md:${md} !important;`
      + `--dfp-shadow-poster:${lg} !important}`,
    );
  }

  return parts.join('\n');
}

const FONT_LINK_TAG_ID = 'dfp-prs-font-link';
const SAFE_FONT_URL = /^https?:\/\/[A-Za-z0-9._\-/:?=&,;%+#]{1,2048}$/;
function applyCustomFontLink(url: string | undefined): void {
  const existing = document.getElementById(FONT_LINK_TAG_ID);
  // Empty / unset → remove any prior tag so toggling off in admin
  // takes effect on next bridge fire.
  if (!url || !SAFE_FONT_URL.test(url.trim())) {
    existing?.remove();
    return;
  }
  const target = url.trim();
  if (existing && (existing as HTMLLinkElement).href === target) return; // no-op
  existing?.remove();
  const link = document.createElement('link');
  link.id = FONT_LINK_TAG_ID;
  link.rel = 'stylesheet';
  link.href = target;
  // crossOrigin omitted on purpose — Google Fonts serves with the
  // right CORS headers; self-hosted endpoints rarely need it.
  document.head.appendChild(link);
}

/** Validated merchant brand swatches as a plain hex[].  Returns
 *  empty array when none configured.  Used by colour pickers that
 *  want to prepend "Tus colores de marca" without consuming the
 *  whole settings module. */
export function getValidBrandSwatches(): string[] {
  const raw = lastApplied?.brandSwatches;
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of raw) {
    if (typeof v !== 'string') continue;
    if (!HEX.test(v)) continue;
    const norm = v.toLowerCase();
    if (seen.has(norm)) continue;  // dedupe
    seen.add(norm);
    out.push(v);
    if (out.length >= 8) break;
  }
  return out;
}

/** Apply text/copy overrides. Targets either:
 *  - explicit `[data-prs-target="key"]` hooks the embed sponsors, or
 *  - a few well-known class selectors (.dfp-trigger-cta, .bd-cta-label,
 *    .bd-tab-label, etc.) so we can override copy on nodes the embed
 *    didn't anticipate.
 *
 *  Run on initial apply AND whenever the embed mounts new nodes (the
 *  MutationObserver in startSettingsBridge re-fires this). */
export function applyPersonalizerText(s: PersonalizerSettingsLike): void {
  // El copy del comercio sólo en SU idioma; en los demás, `undefined`,
  // que `setText`/`setLabel` tratan como «deja lo que hay» — o sea lo
  // que `t()` acaba de pintar en el idioma del cliente.
  const aplica = elCopyDelComercioAplica(s);
  const copy = (v?: string): string | undefined => (aplica ? v : undefined);
  // Logo de la tienda — reemplaza la "K" del cubo superior izquierdo.
  // Re-evaluado en cada mutation del DOM porque el .bd-brand-mark
  // solo existe cuando el overlay está abierto, y eso suele pasar
  // DESPUÉS del initial apply de settings.  Idempotente: si ya
  // tiene el img con la misma src, no toca nada.
  if (typeof s.brandLogoUrl === 'string' && s.brandLogoUrl.trim() !== '') {
    const url = s.brandLogoUrl.trim();
    document.querySelectorAll<HTMLElement>('.bd-brand-mark').forEach((mark) => {
      const existing = mark.querySelector<HTMLImageElement>('img.bd-brand-logo');
      // Compare the raw attribute, NOT `.src`: the property reflects the
      // ABSOLUTIZED URL, so a relative `url` would never match and this
      // block would wipe + re-create the <img> on every observer pulse
      // (feeding the same mutation→re-apply loop the setText guard breaks).
      if (existing && existing.getAttribute('src') === url) return;
      mark.innerHTML = '';
      const img = document.createElement('img');
      img.className = 'bd-brand-logo';
      img.src = url;
      img.alt = s.brandName ?? '';
      img.decoding = 'async';
      img.loading = 'eager';
      img.addEventListener('error', () => {
        mark.innerHTML = 'K';
      }, { once: true });
      mark.appendChild(img);
    });
  }

  // Trigger CTA (closed-state orange button on the PDP).
  //
  // Dos textos, no uno: el botón que ofrece VOLVER a un diseño a medias
  // lo marca index.ts con data-bd-resume, y ese no puede recibir el
  // texto de "empezar". Sin separarlos, la primera aplicación de
  // ajustes —o cualquier pulso del MutationObserver— borraba
  // "Continuar con tu diseño" y devolvía "Personalizar ahora".
  // El NO-resume se deja en `copy(s.ctaLabel)`: en pt devuelve undefined y
  // `setLabel` no toca nada, dejando lo que index.ts ya puso (la base pt)
  // —y sin pisar un `data-cta-label` fijado por el tema—. El resume SÍ se
  // fija siempre (lo hacía ya), por eso su fallback va por IDIOMA
  // (`etiquetaResume`) en vez del literal es «Continuar con tu diseño».
  setLabel('.dfp-trigger-cta:not([data-bd-resume])', '.dfp-trigger-cta-label', copy(s.ctaLabel));
  setLabel('.dfp-trigger-cta[data-bd-resume]', '.dfp-trigger-cta-label',
           copy(s.ctaResumeLabel) || etiquetaResume());

  // Overlay bottom-bar CTA per step (data-cta-step="step1|step2|step3").
  if (s.bottomCtaLabels) {
    setText('[data-cta-step="step1"]', copy(s.bottomCtaLabels.step1));
    setText('[data-cta-step="step2"]', copy(s.bottomCtaLabels.step2));
    setText('[data-cta-step="step3"]', copy(s.bottomCtaLabels.step3));
  }

  // Overlay sidebar tab labels (Diseño / Detalles / Formato).
  //
  // POR POSICIÓN, y por eso sólo con TRES pestañas: estos tres rótulos
  // del comercio se escribieron para el asistente de tres pasos. Una
  // plantilla de dos —las de mascotas, que llevan un paso de diseño y el
  // «Formato»— recibía «Detalles» en la pestaña 2, que es la de formato:
  // el rótulo mentía sobre lo que hay dentro (visto en la Vista cliente,
  // sep-2026). Con otro número de pasos mandan los títulos de la
  // plantilla, que es lo que el wizard ya pinta.
  //
  // Y SÓLO RENOMBRAN LOS PASOS DE SERIE (27-sep-2026). Las placas van en
  // «Fotos · Textos · Formato» (`customerView.splitTextStep`) y, con tres
  // pestañas, este ajuste las volvía a llamar «Diseño» y «Detalles»: el
  // panel decía «Fotos» y la pestaña, otra cosa. Un paso con nombre
  // PROPIO lo ha nombrado la plantilla, y manda ella.
  if (s.stepLabels && document.querySelectorAll('.bd-tab').length === 3) {
    renombrarPasoDeSerie(1, copy(s.stepLabels.step1));
    renombrarPasoDeSerie(2, copy(s.stepLabels.step2));
    renombrarPasoDeSerie(3, copy(s.stepLabels.step3));
  }

  // Tip / save-design / consejo + new editables.
  setText('[data-prs-target="tips-label"]', copy(s.tipsLabel));
  setText('[data-prs-target="save-design-label"]', copy(s.saveDesignLabel));
  setText('[data-prs-target="consejo"]', copy(s.consejoText));

  // Overlay header: brand name + saved badge + close button aria.
  setText('.bd-brand-name', s.brandName);
  setText('.bd-saved-text', copy(s.savedBadgeText));

  // (Logo de la tienda movido a applyPersonalizerText 2026-06-09 —
  // ahora se re-evalúa cada vez que el MutationObserver detecta DOM
  // nuevo, así el logo se inyecta cuando el cliente abre el overlay
  // por primera vez, no sólo en el initial apply pre-mount.)
  const cerrar = copy(s.closeLabel);
  if (cerrar) {
    document
      .querySelectorAll<HTMLButtonElement>('.bd-shell-close')
      .forEach((b) => b.setAttribute('aria-label', cerrar));
  }

  // Overlay header eyebrow ("Personalizando").
  setText('.bd-product-meta-eyebrow', copy(s.eyebrowText));

  // Botón atrás, y las dos herramientas de la cabecera (Consejos ·
  // Guardar). Vivían en una barra bajo el lienzo hasta el 24-sep-2026.
  setText('.bd-back-label', copy(s.backLabel));
  setText('[data-bd-tip-header] .bd-accion-t', copy(s.tipsButtonLabel));
  // El mando de «Guardar» es el que se llamaba «Botón Compartir», y
  // «Compartir» era su valor de fábrica: está escrito en el blob de casi
  // todas las tiendas sin que nadie lo eligiera (el PUT guarda el blob
  // entero). Ese valor viejo no renombra el botón nuevo.
  const guardar = copy(s.shareButtonLabel)?.trim();
  if (guardar && guardar.toLowerCase() !== 'compartir') {
    setText('[data-bd-save-header] .bd-accion-t', guardar);
  }

  // Loading copy in the still-hydrating sidebar panes.
  setText('[data-bd-pane="1"] .bd-pane-loading', copy(s.loadingOptionsText));
  setText('[data-bd-pane="3"] .bd-pane-loading', copy(s.loadingFormatsText));

  // Header product-meta fallback ("Tu diseño") — only override when
  // the element still holds the built-in default. After the embed
  // hydrates with the real product name we must NOT clobber it.
  const metaPorDefecto = copy(s.productMetaDefaultName);
  if (metaPorDefecto) {
    document.querySelectorAll<HTMLElement>('.bd-product-meta-name').forEach((el) => {
      const cur = el.textContent?.trim() ?? '';
      // `cur !== value` guard: if the merchant's default IS «Tu diseño»
      // this would otherwise rewrite (= replace the Text node = childList
      // mutation) on every observer pulse. See setLabel.
      if ((cur === '' || cur === 'Tu diseño') && cur !== metaPorDefecto) {
        el.textContent = metaPorDefecto;
      }
    });
  }

  // Legacy 3-tab hero (only present on templates without `customizationSteps`).
  //
  // SÓLO en los paneles del shell fijo, NUNCA en los del wizard
  // (16-sep-2026). El selector iba a pelo —`.bd-pane-desc`— y pisaba
  // también los tres paneles del wizard, que llevan `data-step-id` y el
  // texto que el comercio escribió en cada sección de la plantilla
  // («Elige la composición.», «Elige el acabado.»). Se veía en las
  // placas: el paso «Formato» rezaba «Elige la forma, el color y los
  // detalles visuales», que es el texto del paso 1 del shell viejo, y el
  // de «Diseño» decía lo mismo en vez de lo suyo. Nadie lo vio antes
  // porque esa línea sólo se enseña cuando el paso tiene una sola fila.
  setText('.bd-pane:not([data-step-id]) .bd-pane-title', copy(s.legacyHeroTitle));
  setText('.bd-pane:not([data-step-id]) .bd-pane-desc', copy(s.legacyHeroSubtext));

  // Feature toggles that hide/show DOM nodes the embed already mounted.
  // The embed itself ALSO consults getCurrentSettings() when deciding
  // whether to mount these in the first place; this is the live-update
  // path (merchant flips a toggle in admin → toolbar reacts).
  toggleHidden('[data-prs-feature="save-design"]', s.enableSaveDesign === false);
  toggleHidden('[data-prs-feature="quick-suggestions"]', s.enableQuickSuggestions === false);
  toggleHidden('[data-prs-feature="style-navigator"]', s.showStyleNavigator === false);

  // Body-level flag for cross-cutting features that don't have a single
  // DOM node we can hide:
  //   * closed-palette → CSS hides the "Cualquier color" custom input
  //     in every colour-picker modal (ui-rows/.../color.ts).
  setBodyFlag('data-prs-closed-palette', s.closedPaletteEnabled === true);
}

/** Apply all overrides + remember the last payload so a later DOM
 *  mutation can re-apply. Idempotent. */
export function applyPersonalizerSettings(s: PersonalizerSettingsLike): void {
  if (!s || typeof s !== 'object') return;
  lastApplied = s;
  // Feed the merchant's copy overrides into the i18n chain BEFORE any
  // form/UI string resolves. Strings already painted keep their text
  // until their next repaint; the customizer awaits settings before
  // buildForm (customizer/runtime.ts) so the form always sees them.
  // Y la tabla de overrides, por lo mismo: son las claves del diccionario
  // reescritas por el comercio EN UN IDIOMA. En otro mercado, fuera.
  setTextOverrides(elCopyDelComercioAplica(s) ? s.texts : undefined);
  applyPersonalizerCss(s);
  applyPersonalizerText(s);
}

/** Boot the bridge: fetch settings for `shop`, apply them, then keep
 *  re-applying text overrides whenever the embed mounts new nodes.
 *  Also listens for `postMessage` / `storage` updates so the admin's
 *  preview iframe gets live updates from the personalizer page.
 *
 *  Idempotent: a second call with the same args is a no-op. */
const LOCAL_STORAGE_KEY = 'dfp_personalizer_settings';

let bridgeStarted = false;
export async function startSettingsBridge(opts: {
  apiBase: string;
  shop: string;
}): Promise<void> {
  if (bridgeStarted) return;
  bridgeStarted = true;

  // Fast-paint path: when running inside the admin preview iframe the
  // parent's /admin/personalizer page mirrors settings to localStorage
  // on every keystroke. Reading it synchronously avoids a FOUC between
  // the trigger CTA mounting and the fetch resolving.  Storefront
  // pages don't write this key, so the read is a harmless no-op there.
  // La clave con el DOMINIO manda: el panel escribe una por tienda y
  // deja la vieja sin sufijo sólo por compatibilidad. Leyendo la vieja
  // con una tienda conocida, la vista previa de una tienda recién dada
  // de alta salía con el logo y los colores de OTRA (sep-2026, visto
  // con la tienda vacía de pruebas: cabecera «Donde fue»).
  const scopedKey = opts.shop ? `${LOCAL_STORAGE_KEY}__${opts.shop}` : LOCAL_STORAGE_KEY;
  try {
    const raw = localStorage.getItem(scopedKey) ?? (opts.shop ? null : localStorage.getItem(LOCAL_STORAGE_KEY));
    if (raw) applyPersonalizerSettings(JSON.parse(raw) as PersonalizerSettingsLike);
  } catch { /* private mode, JSON parse error */ }

  // Live updates from the admin's preview-iframe parent.
  window.addEventListener('message', (e) => {
    const data = e.data as { type?: string; settings?: PersonalizerSettingsLike } | null;
    if (!data || data.type !== 'DFP_SETTINGS' || !data.settings) return;
    applyPersonalizerSettings(data.settings);
  });

  // Cross-tab live updates: a second admin tab editing the same shop
  // pushes via localStorage `storage` event. Same payload shape.
  window.addEventListener('storage', (e) => {
    if (e.key !== scopedKey || !e.newValue) return;
    try {
      applyPersonalizerSettings(JSON.parse(e.newValue) as PersonalizerSettingsLike);
    } catch { /* malformed */ }
  });

  // Re-apply text overrides whenever the embed renders new nodes
  // (trigger CTA, overlay, wizard panes are mounted asynchronously).
  if (typeof MutationObserver !== 'undefined') {
    let scheduled = false;
    const obs = new MutationObserver(() => {
      if (scheduled || !lastApplied) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        if (lastApplied) applyPersonalizerText(lastApplied);
        // Belt and braces against the re-apply loop: the writes above are
        // skip-if-equal (see setLabel/setText), but any future helper that
        // mutates unconditionally would re-trigger this observer with its
        // OWN records and freeze the tab in an infinite microtask loop.
        // Draining the queue here discards exactly the records this
        // re-apply produced — external mutations after this microtask
        // still schedule a fresh pulse.
        obs.takeRecords();
      });
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // Fetch the merchant's actual saved settings. If `shop` is empty
  // (admin demo / standalone mode), skip — the postMessage path is
  // the only source in that case.
  if (opts.shop) {
    try {
      const settings = await fetchPersonalizerSettings(opts.apiBase, opts.shop);
      applyPersonalizerSettings(settings);
    } catch {
      // Already logged in fetchPersonalizerSettings.
    }
  }
}

// ── Internal helpers ──────────────────────────────────────────────────

function setLabel(nodeSel: string, labelSel: string, value: string | undefined): void {
  if (!value) return;
  document.querySelectorAll<HTMLElement>(nodeSel).forEach((node) => {
    const label = node.querySelector<HTMLElement>(labelSel);
    // Skip-if-equal is load-bearing, not an optimisation: assigning
    // `textContent` REPLACES the Text node even when the string is
    // identical, which counts as a childList mutation.  The bridge's
    // MutationObserver re-runs applyPersonalizerText on childList
    // mutations, so an unconditional write here means
    // observer → write → mutation → observer… an infinite microtask
    // loop that freezes the tab at 100% CPU (the «preview.html se
    // congela» bug, 2026-06-10).
    if (label) { if (label.textContent !== value) label.textContent = value; }
    else if (node.textContent !== value) node.textContent = value;
  });
}

/** Los nombres de serie de las tres pestañas, en la forma en que los
 *  pinta el asistente (el título de la sección) o la carcasa (i18n). */
const PASOS_DE_SERIE: Record<number, readonly string[]> = {
  1: ['diseno', 'design'],
  2: ['detalles', 'details'],
  3: ['formato', 'format'],
};
const normalizaRotulo = (t: string): string =>
  t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/^\d+\.?\s*/, '').trim().toLowerCase();

/** El rótulo del comercio para la pestaña `n`, si esa pestaña lleva el
 *  nombre de serie de su posición (o ya lleva el del comercio: los
 *  ajustes se vuelven a aplicar al cambiar en otra pestaña). */
export function renombrarPasoDeSerie(n: 1 | 2 | 3, valor: string | undefined): void {
  if (!valor) return;
  document.querySelectorAll<HTMLElement>(`[data-bd-tab="${n}"] .bd-tab-label`).forEach((el) => {
    const actual = normalizaRotulo(el.textContent ?? '');
    const deSerie = PASOS_DE_SERIE[n]!.includes(actual) || actual === normalizaRotulo(valor);
    if (deSerie && el.textContent !== valor) el.textContent = valor;
  });
}

/** ¿Se aplica el copy que el COMERCIANTE escribió, o manda el
 *  diccionario? Ver `textsLocale` en el tipo de ajustes: esos campos son
 *  UN texto y no uno por idioma, así que en un mercado de otro idioma
 *  pisaban al diccionario y la cabecera salía en castellano sobre un
 *  personalizador en portugués.
 *
 *  Sin `textsLocale` se aplica SIEMPRE, que es lo que ha hecho siempre:
 *  una tienda de un solo idioma no puede perder su copy por esto.
 *
 *  Lo que NO pasa por aquí, a propósito: el nombre de la marca y la URL
 *  del logo, que no son copy sino identidad y valen en cualquier idioma. */
export function elCopyDelComercioAplica(s: PersonalizerSettingsLike): boolean {
  const suyo = (s.textsLocale ?? '').trim().toLowerCase().slice(0, 2);
  return !suyo || suyo === localeDelCliente().slice(0, 2);
}

function setText(sel: string, value: string | undefined): void {
  if (!value) return;
  document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
    // Skip-if-equal — see setLabel for why this guard is mandatory.
    if (el.textContent !== value) el.textContent = value;
  });
}

function toggleHidden(sel: string, hidden: boolean): void {
  document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
    el.hidden = hidden;
  });
}

function setBodyFlag(attr: string, on: boolean): void {
  if (!document.body) return;
  if (on) document.body.setAttribute(attr, '1');
  else document.body.removeAttribute(attr);
}
